import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useCreateProjectTaskMutation } from "../store/api/tasks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Loader2, UserPlus, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { RepeatType, User } from "../types";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format } from "date-fns";
import { ja, enUS } from "date-fns/locale";

interface QuickAddTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectMembers?: User[];  // Pass project members from parent
}

export default function QuickAddTaskModal({
  open,
  onOpenChange,
  projectId,
  projectMembers = [],
}: QuickAddTaskModalProps) {
  const { t, language } = useLanguage();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  // Get current user and workspace from Redux
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspaceId = currentWorkspace?.id || "";
  
  // RTK Query mutation
  const [createProjectTask, { isLoading }] = useCreateProjectTaskMutation();

  const [taskName, setTaskName] = useState("");
  const [description, setDescription] = useState("");
  const [repeatType, setRepeatType] = useState<RepeatType>("none");
  const [initialDueDate, setInitialDueDate] = useState<Date | undefined>(new Date());
  const [errors, setErrors] = useState<{taskName?: string; description?: string}>({});

  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [useMonthEnd, setUseMonthEnd] = useState(false);
  
  const dateLocale = language === 'ja' ? ja : enUS;

  // Reset form when modal opens or closes
  useEffect(() => {
    if (open) {
      setTaskName("");
      setDescription("");
      setRepeatType("none");
      setInitialDueDate(new Date());
      setAssigneeIds([]);
      setUseMonthEnd(false);
      setErrors({});
    } else {
      // Clear state when modal closes to prevent data lingering
      setTaskName("");
      setDescription("");
      setRepeatType("none");
      setInitialDueDate(new Date());
      setAssigneeIds([]);
      setUseMonthEnd(false);
      setErrors({});
    }
  }, [open]);

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  // Use passed project members
  const projectUsers = projectMembers;

  // 月末チェックボックスを表示する頻度タイプ
  const showMonthEndOption = ["monthly", "quarterly", "semi-annually", "yearly"].includes(repeatType);
  const isRepeatable = repeatType !== 'none';

  const handleSubmit = async () => {
    // Validate form
    const newErrors: {taskName?: string; description?: string} = {};

    if (!taskName.trim()) {
      newErrors.taskName = t('tasks.taskNameRequiredError');
    } else if (taskName.length > 50) {
      newErrors.taskName = t('tasks.taskNameMaxLength');
    }

    if (description && description.length > 150) {
      newErrors.description = t('tasks.taskDescMaxLength');
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (!useMonthEnd && !initialDueDate) return;
    if (!workspaceId) return;

    // Format dueDate for API - Use local date format to avoid timezone shifting
    const formattedDueDate = useMonthEnd ? null : (initialDueDate ? format(initialDueDate, "yyyy-MM-dd") : null);

    try {
      await createProjectTask({
        workspaceId,
        projectId,
        data: {
          name: taskName,
          description: description || undefined,
          isRepeatable,
          repeatType,
          useMonthEnd,
          dueDate: formattedDueDate,
          assigneeIds,
          isActive: true,
        },
      }).unwrap();
      
      toast.success(t('tasks.taskCreated') || 'Task created successfully');
      
      // Reset form
      setTaskName("");
      setDescription("");
      setRepeatType("none");
      setInitialDueDate(new Date());
      setAssigneeIds([]); // Clear assignees
      setUseMonthEnd(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error(t('tasks.taskCreateFailed') || 'Failed to create task');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('tasks.quickAddTask')}</DialogTitle>
          <DialogDescription>{t('tasks.quickAddTaskDesc')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* 基本情報セクション */}
          <div className="space-y-4">
            <div className="pb-2 border-b">
              <h3 className="text-gray-900">{t('tasks.basicInfo')}</h3>
              <p className="text-sm text-gray-600">{t('tasks.basicInfoDesc')}</p>
            </div>

            {/* タスク名 */}
            <div>
              <Label className="mb-2" htmlFor="taskName">{t('tasks.taskName')} *</Label>
              <Input
                id="taskName"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                placeholder={t('tasks.taskNamePlaceholder')}
                className={errors.taskName ? "border-red-500" : ""}
                maxLength={50}
                required
              />
              {errors.taskName && (
                <p className="mt-1 text-sm text-red-500">{errors.taskName}</p>
              )}
            </div>

            {/* 説明 */}
            <div>
              <Label className="mb-2" htmlFor="description">{t('common.description')}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('tasks.taskDescPlaceholder')}
                rows={3}
                maxLength={150}
                className={errors.description ? "border-red-500" : ""}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description}</p>
              )}
            </div>

            {/* 担当者選択 */}
            <div>
              <Label className="mb-3 block">{t('tasks.assigneesMultiple')}</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {projectUsers.map((user) => (
                  <div key={user.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`assignee-${user.id}`}
                      checked={assigneeIds.includes(user.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setAssigneeIds([...assigneeIds, user.id]);
                        } else {
                          setAssigneeIds(assigneeIds.filter((id) => id !== user.id));
                        }
                      }}
                    />
                    <label
                      htmlFor={`assignee-${user.id}`}
                      className="text-gray-700 cursor-pointer flex items-center gap-2"
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs">
                        {user.name.charAt(0)}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div>
                          <div className="flex items-center gap-1">
                            {user.name}
                            {user.role === 'guest' && (
                              <UserPlus className="w-3 h-3 text-blue-600" />
                            )}
                          </div>
                          <div className="text-xs text-gray-600">{user.email}</div>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 期日管理セクション */}
          <div className="space-y-4">
            <div className="pb-2 border-b">
              <h3 className="text-gray-900">{t('tasks.dueDateManagement')}</h3>
              <p className="text-sm text-gray-600">{t('tasks.dueDateManagementDesc')}</p>
            </div>

            {/* 繰り返しパターン */}
            <div>
              <Label className="mb-2" htmlFor="repeatType">{t('tasks.frequencyRequired')} </Label>
              <select
                id="repeatType"
                value={repeatType}
                onChange={(e) =>
                  setRepeatType(e.target.value as any)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="none">{t('tasks.none')}</option>
                <option value="daily">{t('tasks.daily')}</option>
                <option value="weekly">{t('tasks.weekly')}</option>
                <option value="monthly">{t('tasks.monthly')}</option>
                <option value="quarterly">{t('tasks.quarterly')}</option>
                <option value="semi-annually">{t('tasks.semiAnnually')}</option>
                <option value="yearly">{t('tasks.yearly')}</option>
              </select>
            </div>

            {/* 初回締切日 */}
            <div>
              <Label className="mb-2" htmlFor="initialDeadline">{t('tasks.initialDeadline')} </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    disabled={useMonthEnd}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {initialDueDate ? (
                      format(initialDueDate, "PPP", { locale: dateLocale })
                    ) : (
                      <span className="text-gray-500">
                        {language === 'ja' ? '日付を選択' : 'Select date'}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={initialDueDate}
                    onSelect={setInitialDueDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {!useMonthEnd && (
                <p className="text-xs text-gray-600 mt-1">
                  {t('tasks.deadlineHint')}
                  {repeatType === "daily" && t('tasks.deadlineHintDaily')}
                  {repeatType === "weekly" && t('tasks.deadlineHintWeekly')}
                  {repeatType === "monthly" && t('tasks.deadlineHintMonthly')}
                  {repeatType === "quarterly" && t('tasks.deadlineHintQuarterly')}
                  {repeatType === "semi-annually" && t('tasks.deadlineHintSemiAnnually')}
                  {repeatType === "yearly" && t('tasks.deadlineHintYearly')}
                  {repeatType === "none" && t('tasks.deadlineHintNone')}
                  {t('tasks.deadlineHintSuffix')}
                </p>
              )}
            </div>

            {/* 月末チェックボックス */}
            {showMonthEndOption && (
              <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="use-month-end"
                  checked={useMonthEnd}
                  onCheckedChange={(checked) => {
                    setUseMonthEnd(!!checked);
                  }}
                />
                <label
                  htmlFor="use-month-end"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  {t('tasks.useMonthEnd')}
                </label>
              </div>
            )}
          </div>

          {/* 完了トリガー方式の説明 */}
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-blue-900 mb-1">{t('tasks.completionTriggerInfo')}</p>
            <p className="text-blue-700 text-sm">
              {t('tasks.completionTriggerDetail')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!taskName.trim() || (!useMonthEnd && !initialDueDate) || isLoading}
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
